import { createContext, type ComponentChildren } from 'preact';
import { useContext, useEffect, useMemo, useState } from 'preact/hooks';

const isClient = typeof window === 'object';

export const MOBILE_QUERY = '(max-width: 479px)';
export const TABLET_QUERY = '(min-width: 480px) and (max-width: 1023px)';
export const DESKTOP_QUERY = '(min-width: 1024px)';
export const REDUCED_MOTION_QUERY = '(prefers-reduced-motion: reduce)';

type ViewportContextType =
  | {
      isMobile: boolean;
      isTablet: boolean;
      isDesktop: boolean;
      reducedMotion: boolean;
    }
  | undefined;
const ViewportContext = createContext<ViewportContextType>(undefined);

function matchMediaQuery(query: string) {
  const [matches, setMatches] = useState(
    isClient ? window.matchMedia(query).matches : false
  );

  useEffect(() => {
    const mediaQueryList = window.matchMedia(query);

    const handler = (event: MediaQueryListEvent) => {
      setMatches(event.matches);
    };

    mediaQueryList.addEventListener('change', handler);
    setMatches(mediaQueryList.matches);

    return () => {
      mediaQueryList.removeEventListener('change', handler);
    };
  }, [query]);

  return matches;
}

export type MediaQueryProviderProps = {
  children: ComponentChildren;
};

export function MediaQueryProvider({ children }: MediaQueryProviderProps) {
  const isMobile = matchMediaQuery(MOBILE_QUERY);
  const isTablet = matchMediaQuery(TABLET_QUERY);
  const isDesktop = matchMediaQuery(DESKTOP_QUERY);
  const reducedMotion = matchMediaQuery(REDUCED_MOTION_QUERY);

  const value = useMemo(
    () => ({ isMobile, isTablet, isDesktop, reducedMotion }),
    [isMobile, isTablet, isDesktop, reducedMotion]
  );

  return (
    <ViewportContext.Provider value={value}>
      {children}
    </ViewportContext.Provider>
  );
}

export default function useMediaQuery() {
  const context = useContext(ViewportContext);

  if (context === undefined) {
    throw new Error('useMediaQuery must be used within MediaQueryProvider');
  }

  return context;
}

// Helper to use it without hook
export function isMobileSize() {
  return window.matchMedia(MOBILE_QUERY).matches;
}
